import { ChangeDetectorRef, Component, DestroyRef, Inject, OnInit, computed, inject } from '@angular/core';

import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { CustomLoadingIndicatorService } from '@frontend/sports/loading-indicator-feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { UserService } from '@frontend/sports/user/feature';
import { LocalStoreService } from '@frontend/vanilla/core';

import { HELP_BOX_TYPE } from '../help-box/help-box-type';
import { HelpBoxService } from '../help-box/help-box.service';
import { IBetSlipRepositoryResult, MigrationTabType } from '../my-bets-base/models/my-bets-viewmodels';
import { PopupManager } from '../popup/popup-manager.service';
import { MyBetsListService } from './components/betslip-list/my-bets-list.service';
import { MyBetsDateRangeFilterContstraint, MyBetsFilterConstraint, MyBetsMigrationFilterContstraint } from './models/my-bets-filter-constraints';
import { AssociatedAccountService } from './services/associated-account-service';
import { MyBetsBetslipRepository } from './services/my-bets-betslip.repository';
import { MyBetsEditBetHistoryService } from './services/my-bets-edit-bet-history.service';
import { StadiumBetsVisibilityService } from './services/stadium-bets-visibility.service';

@Component({
    template: '',
    standalone: false,
})
export abstract class MyBetsViewBaseComponent implements OnInit {
    isInitalLoadReady = false;
    isAutoCashoutHelpBoxVisible = false;
    filterConstraint: MyBetsFilterConstraint = new MyBetsFilterConstraint();
    autoCashoutHelpBoxStorageKey = 'autocashoutHelpBoxClosed';
    shareMyBetHelpBoxStorageKey = 'shareMyBetHelpBoxClosed';
    cashoutInfoHelpBoxStorageKey = 'cashoutInfoHelpBoxClosed';
    showMoreVisible = true;
    private myBetsTrackSuffix = '';
    showMoreClicked = false;
    associatedAccount: Nullable<string>;
    betType = HELP_BOX_TYPE;

    data: IBetSlipRepositoryResult = {
        betslips: [],
        hasMore: false,
        pageIndex: 1,
        errorLoadingBets: false,
    };

    protected migrationTabType: MigrationTabType | null = null;
    protected readonly destroyRef = inject(DestroyRef);

    protected isEditBetHistory = computed(() => {
        return this.editBetHistoryService.hasEditBetHistoryBetslips;
    });

    constructor(
        protected loadingIndicatorService: CustomLoadingIndicatorService,
        @Inject(String) protected token: string,
        protected repository: MyBetsBetslipRepository,
        protected mybetsConfig: MyBetsConfig,
        protected helpBoxService: HelpBoxService,
        protected localStorage: LocalStoreService,
        protected editBetHistoryService: MyBetsEditBetHistoryService,
        protected userService: UserService,
        protected trackingService: TrackingService,
        protected myBetsListService: MyBetsListService,
        @Inject(Boolean) protected inColumn: boolean,
        protected sitecoreMyBets: SitecoreMyBetsConfig,
        protected popupManager: PopupManager,
        protected associatedAccountService: AssociatedAccountService,
        protected stadiumMyBetsService: StadiumBetsVisibilityService,
        protected changeRef: ChangeDetectorRef,
    ) {
        this.myBetsTrackSuffix = inColumn ? '_Mini_MyBets' : '_Main_MyBets';
    }

    ngOnInit(): void {
        this.getAssociatedAccount();
    }

    reload = async (): Promise<void> => {
        this.data.betslips = [];
        this.isInitalLoadReady = false;
        this.isAutoCashoutHelpBoxVisible = false;
        const indicator = this.loadingIndicatorService.start(this.token);

        try {
            this.data = await this.repository.getBetSlips({
                pageSize: this.mybetsConfig.pageSize,
                filterConstraint: this.getFilter(),
                listType: this.data.listType!,
            });

            this.isAutoCashoutHelpBoxVisible = this.getIsAutoCashoutHelpBoxVisible();
        } finally {
            this.isInitalLoadReady = true;
            this.showMoreVisible = this.data.hasMore;
            this.showMoreClicked = false;
            indicator.done();
            this.changeRef.detectChanges();
        }
    };

    private getFilter(): MyBetsFilterConstraint {
        const isSettledTab = this.data.listType === BetslipListType.Settled;

        const filter = isSettledTab
            ? (this.filterConstraint.removeConstraint(MyBetsMigrationFilterContstraint), this.filterConstraint)
            : new MyBetsFilterConstraint();

        if (this.migrationTabType === MigrationTabType.Before) {
            filter.addConstraint(
                this.repository.getBeforeFilter(
                    this.associatedAccount!,
                    !!this.filterConstraint.getConstraint(MyBetsDateRangeFilterContstraint),
                    isSettledTab,
                ),
            );
        }

        return filter;
    }

    protected getAssociatedAccount(): void {
        this.associatedAccount = this.associatedAccountService.getAssociatedAccount();
    }

    get isAuthenticated(): boolean {
        return this.userService.isAuthenticated;
    }

    get showBetSlipList(): boolean {
        return this.data.betslips.length > 0 || (this.data.listType === BetslipListType.Settled && this.filterConstraint.constraints.length > 0);
    }

    get showArchivedBets(): boolean {
        return this.stadiumMyBetsService.showArchivedBets(this.data.listType);
    }

    get showTransactionBets(): boolean {
        return this.stadiumMyBetsService.showTransactionBets(this.data.listType);
    }

    get showReBetHelpBox(): boolean {
        return this.stadiumMyBetsService.showReBetHelpBox(this.data.listType);
    }

    get showMigrationTabs(): boolean {
        return !!this.associatedAccount;
    }

    getIsAutoCashoutHelpBoxVisible(): boolean {
        return (
            this.helpBoxService.isHelpBoxEnabled(HELP_BOX_TYPE.AutoCashout) &&
            !this.localStorage.get(this.autoCashoutHelpBoxStorageKey) &&
            (this.data.listType === BetslipListType.Live || this.data.listType === BetslipListType.Open) &&
            this.data.betslips.length > 0
        );
    }

    get isShareMyBetHelpBoxVisible(): boolean {
        return (
            this.helpBoxService.isHelpBoxEnabled(HELP_BOX_TYPE.ShareMyBet) &&
            !this.localStorage.get(this.shareMyBetHelpBoxStorageKey) &&
            (this.data.listType === BetslipListType.Live || this.data.listType === BetslipListType.Open) &&
            this.data.betslips.length > 0
        );
    }

    get isCashOutInfoVisible(): boolean {
        return (
            this.helpBoxService.isHelpBoxEnabled(HELP_BOX_TYPE.CashOutInfo) &&
            !this.localStorage.get(this.cashoutInfoHelpBoxStorageKey) &&
            (this.data.listType === BetslipListType.Live || this.data.listType === BetslipListType.Open) &&
            this.data.betslips.length > 0
        );
    }

    async loadMoreBetSlips(): Promise<void> {
        if (this.myBetsListService.columns !== 1 && !this.showMoreClicked) {
            return;
        }
        try {
            this.data = await this.repository.getNextBetslips({
                currentData: this.data,
                filterConstraint: this.getFilter(),
                pageSize: this.mybetsConfig.pageSize,
            });
        } catch {
        } finally {
            this.showMoreVisible = this.data.hasMore;
            this.showMoreClicked = false;
            this.changeRef.markForCheck();
        }
    }

    onFilterChanged(): Promise<void> {
        return this.reload();
    }

    onBetUnpinned = () => {
        this.reload();
    };

    onAutoCashoutHelpBoxClose = () => {
        this.localStorage.set(this.autoCashoutHelpBoxStorageKey, true);
        this.isAutoCashoutHelpBoxVisible = false;
    };

    onAutoCashoutHelpInfoIconClick = () => {
        this.trackingService.track(trackingConstants.EVENT_AUTO_EARLYPAYOUT_SET, {
            [trackingConstants.COMPONENT_AUTO_EARLYPAYOUT]: `AutoEarlyPayout_SetFlyOut_Info_Click${this.myBetsTrackSuffix}`,
        });
    };

    onShareMyBetHelpBoxClose = () => {
        this.localStorage.set(this.shareMyBetHelpBoxStorageKey, true);
    };

    onCashoutInfoHelpBoxClose = () => {
        this.localStorage.set(this.cashoutInfoHelpBoxStorageKey, true);
    };

    onShowMoreClick(): void {
        this.showMoreClicked = true;
        this.loadMoreBetSlips();
    }
}
